import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, getCardClass, isCardTransparent, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function BadgesSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle = 'shadow' }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'grid';
    const items = content.items || [];
    const cStyle = config.card_style || cardStyle;
    const cardCls = getCardClass(cStyle, s.dark);
    const isTransparent = isCardTransparent(cStyle, s.dark);

    if (items.length === 0) return null;

    // ── Inline variant ──
    if (variant === 'inline') {
        return (
            <section className={`py-12 md:py-16 relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                        {items.map((item, i) => (
                            <div key={i}
                                className={`flex items-center gap-3 px-5 py-3 rounded-full border transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} shadow-sm`}
                                style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms', backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb' }}>
                                {item.icon && <span className="text-2xl">{item.icon}</span>}
                                <div>
                                    <p className="text-sm font-semibold" style={{ color: s.text }}>{item.title}</p>
                                    {item.description && <p className="text-xs" style={{ color: s.textSub }}>{item.description}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Grid variant (default) ──
    const badgeColumns = config.columns || 4;
    const cols = badgeColumns === 2 ? 'grid-cols-1 md:grid-cols-2' : badgeColumns === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';

    return (
        <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div className={`grid ${cols} gap-6`}>
                    {items.map((item, i) => (
                        <div key={i}
                            className={`${cardCls} p-6 text-center transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                            style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                            {item.icon && (
                                <div className="text-4xl mb-4 mx-auto w-16 h-16 rounded-2xl flex items-center justify-center"
                                    style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : hexToRgba(s.accent, 0.06) }}>
                                    {item.icon}
                                </div>
                            )}
                            <h3 className="text-lg font-bold mb-2" style={{ color: isTransparent ? s.text : undefined }}>{item.title}</h3>
                            {item.description && <p className="text-sm" style={{ color: s.textSub }}>{item.description}</p>}
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
        </section>
    );
}
